<script setup lang="ts">
  import { useLocale } from '@tundr/i18n';
  import { computed, useId } from 'vue';
  import { AttributeList } from '../AttributeList';
  import { AttributeListSurface } from '../AttributeList/AttributeList.types';
  import { FontIcon } from '../FontIcon';
  import { amountsClass, rootClass, titleIconClass } from './OrderSummary.css';
  import type {
    OrderSummaryProps,
    OrderSummarySlots,
  } from './OrderSummary.types';

  const props = withDefaults(defineProps<OrderSummaryProps>(), {
    columns: 1,
    titleAs: 'h2',
  });

  defineSlots<OrderSummarySlots>();

  const { t } = useLocale('ds-components');

  const uid = useId();
  const resolvedTestId = computed(
    () => props.testId ?? `ds-order-summary-${uid}`,
  );
</script>

<template>
  <div :class="rootClass" :data-testid="resolvedTestId">
    <AttributeList
      :title="title"
      :title-as="titleAs"
      :columns="columns"
      :test-id="`${resolvedTestId}-details`"
    >
      <template v-if="icon" #leading>
        <FontIcon
          :icon-name="icon"
          :class="titleIconClass"
          aria-hidden="true"
        />
      </template>
      <slot />
    </AttributeList>
    <AttributeList
      v-if="$slots.amounts"
      :class="amountsClass"
      :surface="AttributeListSurface.Raised"
      :aria-label="t('order_summary.amounts_aria_label')"
      :test-id="`${resolvedTestId}-amounts`"
    >
      <slot name="amounts" />
    </AttributeList>
  </div>
</template>
